// ============================================================================
// PREVIEW AND APPLY A DUE-DATE EXTENSION (ADR-0021 §7)
// ============================================================================
// Shows exactly which obligations a recorded notification covers, and what
// applying it would do to each — INCLUDING the ones it would skip, and why.
//
// Preview and apply hit the same server-side evaluation, so what is shown here
// is not a second implementation that can drift from the one that writes.
//
// The skip reasons are shown rather than filtered out on purpose. "0 applied"
// with no explanation is the report that sends someone hunting through the
// database; "24 would move the deadline earlier — check the date" is the one
// that gets the typo fixed.

import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutGrid, type SyutGridColumn } from 'components/ui';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { gstExtensionService } from 'api/services/gstExtensionService';
import type {
  ObligationExtension,
  ObligationExtensionApplyResult,
  ObligationExtensionMatch,
} from 'api/types/obligationExtension.types';

interface Props {
  isOpen: boolean;
  extension: ObligationExtension | null;
  onClose: () => void;
  /** Fired after a successful apply so the list can pick up applied_at. */
  onApplied: () => void;
  /** Whether the caller may apply (the `edit` action). */
  canApply?: boolean;
}

const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const Stat: React.FC<{ label: string; value: number; tone?: 'good' | 'warn' | 'muted' }> = ({
  label, value, tone = 'muted',
}) => {
  const colour = tone === 'good' ? '#16a34a' : tone === 'warn' ? '#d97706' : 'var(--text-secondary)';
  return (
    <div
      style={{
        border: '1px solid var(--border-color)', borderRadius: 6,
        padding: '0.5rem 0.75rem', minWidth: 120, flex: '0 0 auto',
      }}
    >
      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: colour, lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{label}</div>
    </div>
  );
};

const GstExtensionPreviewModal: React.FC<Props> = ({
  isOpen, extension, onClose, onApplied, canApply = false,
}) => {
  const [result, setResult] = useState<ObligationExtensionApplyResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    if (!extension) return;
    setLoading(true);
    try {
      setResult(await gstExtensionService.preview(extension.id));
    } catch (err: any) {
      notificationService.toast.error(
        err?.response?.data?.message ?? 'Could not preview this extension.',
      );
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [extension]);

  useEffect(() => { if (isOpen) void load(); }, [isOpen, load]);

  const handleApply = useCallback(async () => {
    if (!extension) return;
    setConfirmOpen(false);
    setApplying(true);
    try {
      const applied = await gstExtensionService.apply(extension.id);
      setResult(applied);

      // Reports the whole outcome, not just the happy count. An apply that
      // matched nothing must read as such rather than as success.
      const parts = [`${applied.stamped} extended`];
      if (applied.alreadyApplied > 0) parts.push(`${applied.alreadyApplied} already applied`);
      if (applied.skippedNotLater > 0) parts.push(`${applied.skippedNotLater} would move earlier`);
      if (applied.skippedNoDueDate > 0) parts.push(`${applied.skippedNoDueDate} without a statutory date`);

      if (applied.stamped === 0) notificationService.toast.warning(parts.join(', ') + '.');
      else notificationService.toast.success(parts.join(', ') + '.');

      onApplied();
    } catch (err: any) {
      notificationService.toast.error(
        err?.response?.data?.message ?? 'Could not apply this extension.',
      );
    } finally {
      setApplying(false);
    }
  }, [extension, onApplied]);

  const columns: SyutGridColumn<ObligationExtensionMatch>[] = [
    {
      id: 'client',
      header: 'Client',
      accessorKey: 'clientLegalName',
      size: 220,
      enableSorting: true,
      cell: ({ row }) => row.original.clientLegalName || '—',
    },
    {
      id: 'gstin',
      header: 'GSTIN',
      accessorKey: 'registrationNumber',
      size: 170,
      enableSorting: true,
      cell: ({ row }) => row.original.registrationNumber || '—',
    },
    { id: 'formCode', header: 'Return', accessorKey: 'formCode', size: 90, enableSorting: true },
    { id: 'periodLabel', header: 'Period', accessorKey: 'periodLabel', size: 120, enableSorting: true },
    {
      id: 'currentDueDate',
      header: 'Currently due',
      accessorKey: 'currentDueDate',
      size: 130,
      enableSorting: true,
      cell: ({ row }) => formatDate(row.original.currentDueDate),
    },
    {
      id: 'outcome',
      header: 'Becomes',
      accessorKey: 'skipReason',
      size: 280,
      enableSorting: true,
      cell: ({ row }) => (
        row.original.skipReason
          ? (
            <span style={{ color: '#d97706', fontSize: '0.8125rem' }}>
              {row.original.skipReason}
            </span>
          )
          : (
            <strong style={{ color: '#16a34a' }}>{formatDate(row.original.newDueDate)}</strong>
          )
      ),
    },
  ];

  if (!isOpen || !extension) return null;

  const alreadyApplied = !!extension.appliedAt;

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-xl">
        {(loading || applying) && (
          <div
            style={{
              position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
            }}
          >
            <div
              style={{
                width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
              }}
            />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              {applying ? 'Applying…' : 'Evaluating scope…'}
            </span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {extension.notificationRef} — {extension.formCode} · due {formatDate(extension.newDueDate)}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            <button className="modal-close" onClick={onClose} title="Close">
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <div
            style={{
              display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12,
            }}
          >
            <Stat
              label={result?.preview ? 'would be extended' : 'extended'}
              value={result?.stamped ?? 0}
              tone="good"
            />
            <Stat label="already applied" value={result?.alreadyApplied ?? 0} />
            <Stat label="would move earlier" value={result?.skippedNotLater ?? 0} tone="warn" />
            <Stat label="no statutory date" value={result?.skippedNoDueDate ?? 0} tone="warn" />
            <Stat label="in scope" value={result?.matches.length ?? 0} />
          </div>

          {alreadyApplied && (
            <div
              style={{
                border: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
                borderRadius: 6, padding: '0.5rem 0.75rem', marginBottom: 10,
                fontSize: '0.8125rem', color: 'var(--text-secondary)',
              }}
            >
              Applied {formatDate(extension.appliedAt)} to{' '}
              <strong style={{ color: 'var(--text-primary)' }}>{extension.appliedCount ?? 0}</strong>{' '}
              obligation(s). Applying again is safe — it only picks up periods generated since,
              which is why an extension recorded before a client was onboarded still reaches them.
            </div>
          )}

          {!loading && result && result.matches.length === 0 && (
            <div
              style={{
                border: '1px solid #f59e0b', background: 'var(--bg-secondary)',
                borderRadius: 6, padding: '0.75rem', marginBottom: 10, fontSize: '0.8125rem',
                color: 'var(--text-secondary)',
              }}
            >
              <strong style={{ color: 'var(--text-primary)' }}>Nothing is in scope.</strong>{' '}
              No obligation matches this return, period and scope. The commonest causes are a
              period start that is not the first month of the period, a state list that no
              registration falls in, or a scheme that none of them file under.
            </div>
          )}

          <SyutGrid
            data={(result?.matches ?? []) as any}
            columns={columns as any}
            enableSorting
            enablePagination
            pageSize={25}
            emptyMessage={loading ? 'Evaluating…' : 'Nothing in scope.'}
          />
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={applying}>Close</button>
          {canApply && (
            <button
              className="btn btn-primary"
              onClick={() => setConfirmOpen(true)}
              disabled={applying || loading || (result?.stamped ?? 0) === 0}
              title={
                (result?.stamped ?? 0) === 0
                  ? 'Nothing left to extend'
                  : 'Move the deadline on every obligation listed'
              }
            >
              Apply to {result?.stamped ?? 0}
            </button>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Apply this extension?"
        message={
          `${result?.stamped ?? 0} obligation(s) will move to ${formatDate(extension.newDueDate)}, `
          + 'citing ' + extension.notificationRef + '. Already-filed periods are included — '
          + 'lateness is worked out from the dates, so extending after a filing corrects the '
          + 'judgement rather than needing a sweep. This can be revoked.'
        }
        confirmLabel="Apply"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={() => { void handleApply(); }}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default GstExtensionPreviewModal;
